import { BaseMapper } from '@ps/utils';
import {
  CompanySupplierSettingsDto,
  SupplierMicroSurveySettingDto,
  SupplierVariableMappingDto,
} from '../dtos/company-supplier-settings.dto';
import {
  MicroSurveySettingsForm,
  SupplierSettingsForm,
  SupplierVariableMappingForm,
  VariableMappingFieldForm,
} from '../../models/company-form.model';
import {
  SUPPLIER_VARIABLE_MAPPING_DEFAULTS,
  SUPPLIER_VARIABLE_MAPPING_DTO_KEYS,
} from '../supplier-variable-mapping.constants';

export class CompanySupplierSettingsMapper extends BaseMapper<
  CompanySupplierSettingsDto,
  SupplierSettingsForm
> {
  public fromDto(dto: CompanySupplierSettingsDto): SupplierSettingsForm {
    return {
      ...this._mapGeneralSettings(dto),
      ...this._mapRedirectUrls(dto),
      ...this._mapHashingSettings(dto),
      ...this._mapS2sSettings(dto),
      isNotify: dto.isNotify ?? false,
      microSurveySetting: this._mapMicroSurvey(dto),
      variableMapping: this._mapVariableMapping(dto.variable_mapping),
    };
  }

  private _mapGeneralSettings(
    dto: CompanySupplierSettingsDto,
  ): Pick<
    SupplierSettingsForm,
    | 'supplierType'
    | 'dynamicProfiler'
    | 'isSegmentationEnabled'
    | 'isPrivacyPolicyActive'
    | 'cookieYesIntegration'
    | 'hemsOnPixels'
    | 'isDataQualityQuestionsActive'
    | 'isAsrRedirectionEnable'
    | 'skipPreScreener'
    | 'isScreenerV2'
  > {
    return {
      supplierType: dto.supplier_type === 'public' ? 'public' : 'private',
      dynamicProfiler: dto.dynamicProfiler ?? false,
      isSegmentationEnabled: dto.isSegmentationEnabled ?? false,
      isPrivacyPolicyActive: dto.isPrivacyPolicyActive ?? false,
      cookieYesIntegration: dto.cookieYesIntegration ?? false,
      hemsOnPixels: dto.hemsOnPixels ?? false,
      isDataQualityQuestionsActive: dto.isDataQualityQuestionsActive ?? false,
      isAsrRedirectionEnable: dto.isAsrRedirectionEnable ?? false,
      skipPreScreener: dto.skipPreScreener ?? false,
      isScreenerV2: dto.isScreenerV2 ?? false,
    };
  }

  private _mapRedirectUrls(
    dto: CompanySupplierSettingsDto,
  ): Pick<
    SupplierSettingsForm,
    | 'suppUrlCompletes'
    | 'suppUrlOverquota'
    | 'suppUrlTerms'
    | 'suppUrlSecurityTerms'
  > {
    return {
      suppUrlCompletes: dto.supp_url_completes ?? '',
      suppUrlOverquota: dto.supp_url_overquota ?? '',
      suppUrlTerms: dto.supp_url_terms ?? '',
      suppUrlSecurityTerms: dto.supp_url_security_terms ?? '',
    };
  }

  private _mapHashingSettings(
    dto: CompanySupplierSettingsDto,
  ): Pick<
    SupplierSettingsForm,
    'isHashing' | 'isHashingInbound' | 'privateKey'
  > {
    return {
      isHashing: dto.isHashing ?? false,
      isHashingInbound: dto.isHashingInbound ?? false,
      privateKey: dto.private_key ?? '',
    };
  }

  private _mapS2sSettings(
    dto: CompanySupplierSettingsDto,
  ): Pick<
    SupplierSettingsForm,
    | 'iss2sCallback'
    | 'isCompleteToNonCompleteURL'
    | 'isNonCompleteToCompleteURL'
    | 's2sMicroRewardCallbackURL'
  > {
    return {
      iss2sCallback: dto.iss2sCallback ?? false,
      isCompleteToNonCompleteURL: dto.isCompleteToNonCompleteURL ?? '',
      isNonCompleteToCompleteURL: dto.isNonCompleteToCompleteURL ?? '',
      s2sMicroRewardCallbackURL: dto.s2sMicroRewardCallbackURL ?? '',
    };
  }

  private _mapMicroSurvey(
    dto: CompanySupplierSettingsDto,
  ): MicroSurveySettingsForm {
    const microSurveySetting = dto.microSurveySetting;
    return {
      ...this._mapMicroSurveyDetails(microSurveySetting),
      ...this._mapMicroSurveyAmounts(microSurveySetting),
    };
  }

  private _mapMicroSurveyDetails(
    ms: SupplierMicroSurveySettingDto | undefined,
  ): Pick<
    MicroSurveySettingsForm,
    'active' | 'showConsent' | 'showMicrosurveySettings' | 'currencyName'
  > {
    return {
      active: ms?.active ?? false,
      showConsent: ms?.showConsent ?? true,
      showMicrosurveySettings: ms?.showMicrosurveySettings ?? false,
      currencyName: ms?.currencyName ?? '',
    };
  }

  private _mapMicroSurveyAmounts(
    ms: SupplierMicroSurveySettingDto | undefined,
  ): Pick<MicroSurveySettingsForm, 'conversionValue' | 'rewardAmount'> {
    return {
      conversionValue: ms?.conversionValue ?? null,
      rewardAmount: ms?.rewardAmount ?? null,
    };
  }

  private _indexVariableMapping(
    rawList: SupplierVariableMappingDto[] | undefined,
  ): Record<string, VariableMappingFieldForm> {
    const index: Record<string, VariableMappingFieldForm> = {};

    for (const item of rawList ?? []) {
      for (const [key, val] of Object.entries(item)) {
        index[key] = {
          incoming: val.incoming ?? '',
          outgoing: val.outgoing ?? '',
        };
      }
    }

    return index;
  }

  private _mapVariableMapping(
    rawList: SupplierVariableMappingDto[] | undefined,
  ): SupplierVariableMappingForm {
    const index = this._indexVariableMapping(rawList);

    return {
      psSupplierSid:
        index[SUPPLIER_VARIABLE_MAPPING_DTO_KEYS.psSupplierSid] ??
        SUPPLIER_VARIABLE_MAPPING_DEFAULTS.psSupplierSid,
      psSupplierRespondentId:
        index[SUPPLIER_VARIABLE_MAPPING_DTO_KEYS.psSupplierRespondentId] ??
        SUPPLIER_VARIABLE_MAPPING_DEFAULTS.psSupplierRespondentId,
      psCustomSvar1:
        index[SUPPLIER_VARIABLE_MAPPING_DTO_KEYS.psCustomSvar1] ??
        SUPPLIER_VARIABLE_MAPPING_DEFAULTS.psCustomSvar1,
      psCustomSvar2:
        index[SUPPLIER_VARIABLE_MAPPING_DTO_KEYS.psCustomSvar2] ??
        SUPPLIER_VARIABLE_MAPPING_DEFAULTS.psCustomSvar2,
      psTermRsn:
        index[SUPPLIER_VARIABLE_MAPPING_DTO_KEYS.psTermRsn] ??
        SUPPLIER_VARIABLE_MAPPING_DEFAULTS.psTermRsn,
      psPsid:
        index[SUPPLIER_VARIABLE_MAPPING_DTO_KEYS.psPsid] ??
        SUPPLIER_VARIABLE_MAPPING_DEFAULTS.psPsid,
      psRstatus:
        index[SUPPLIER_VARIABLE_MAPPING_DTO_KEYS.psRstatus] ??
        SUPPLIER_VARIABLE_MAPPING_DEFAULTS.psRstatus,
      psHash:
        index[SUPPLIER_VARIABLE_MAPPING_DTO_KEYS.psHash] ??
        SUPPLIER_VARIABLE_MAPPING_DEFAULTS.psHash,
      psOqid:
        index[SUPPLIER_VARIABLE_MAPPING_DTO_KEYS.psOqid] ??
        SUPPLIER_VARIABLE_MAPPING_DEFAULTS.psOqid,
      psCpi:
        index[SUPPLIER_VARIABLE_MAPPING_DTO_KEYS.psCpi] ??
        SUPPLIER_VARIABLE_MAPPING_DEFAULTS.psCpi,
      psSurveyid:
        index[SUPPLIER_VARIABLE_MAPPING_DTO_KEYS.psSurveyid] ??
        SUPPLIER_VARIABLE_MAPPING_DEFAULTS.psSurveyid,
    };
  }
}
